import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Clock, FileText, CheckSquare, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = {
  name: '',
  branch_ids: [],
  all_branches: true,
  invoice_status: [],
  days_after: '',
  target_record_ids: [],
  all_records: true,
  kdv_odeme_gunu: '',
  bildirim_tipi: 'her_gun',
  bildirim_suresi: ''
};

const AccountingInvoiceWarningSettings = ({ ruleType, title, readOnly = false }) => {
  const [rules, setRules] = useState([]);
  const [branches, setBranches] = useState([]);
  const [pendingRecords, setPendingRecords] = useState([]);
  const [recordSearch, setRecordSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json',
  });

  const loadRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/accounting/warning-rules?rule_type=${ruleType}`, { headers: getAuthHeaders() });
      setRules(res.data || []);
    } catch (e) {
      console.error(`${title} kuralları yüklenemedi:`, e);
    }
  }, [ruleType, title]);

  const loadBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data || []);
    } catch (e) {
      console.error('Şubeler yüklenemedi:', e);
    }
  }, []);

  const loadPendingRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/accounting/pending-invoices?type=${ruleType}`, { headers: getAuthHeaders() });
      setPendingRecords(res.data || []);
    } catch (e) {
      console.error('Bekleyen işlemler yüklenemedi:', e);
    }
  }, [ruleType]);

  useEffect(() => {
    loadRules();
    loadBranches();
    loadPendingRecords();
  }, [loadRules, loadBranches, loadPendingRecords]);

  const handleOpenModal = (rule = null) => {
    if (readOnly) return;
    setRecordSearch('');
    if (rule) {
      setEditingId(rule.id);
      setForm({
        name: rule.name || '',
        branch_ids: rule.branch_ids || [],
        all_branches: !rule.branch_ids || rule.branch_ids.length === 0,
        invoice_status: rule.invoice_status || [],
        days_after: rule.days_after !== null ? rule.days_after : '',
        target_record_ids: rule.target_record_ids || [],
        all_records: !rule.target_record_ids || rule.target_record_ids.length === 0,
        kdv_odeme_gunu: rule.kdv_odeme_gunu || '',
        bildirim_tipi: rule.bildirim_tipi || 'her_gun',
        bildirim_suresi: rule.bildirim_suresi || ''
      });
    } else {
      setEditingId(null);
      setForm(emptyForm);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Kural adı zorunludur.');
    if (ruleType !== 'kdv_odemesi') {
      if (form.invoice_status.length === 0) return toast.error('En az bir fatura durumu seçmelisiniz.');
      if (form.days_after === '') return toast.error('Gün sayısı zorunludur.');
    } else {
      if (!form.kdv_odeme_gunu) return toast.error('KDV ödeme günü zorunludur.');
      if (form.bildirim_tipi === 'gun_kala' && form.bildirim_suresi === '') {
        return toast.error('Bildirim süresi zorunludur.');
      }
    }

    const payload = {
      rule_type: ruleType,
      name: form.name,
      branch_ids: form.all_branches ? [] : form.branch_ids,
      target_record_ids: form.all_records ? [] : form.target_record_ids,
      invoice_status: form.invoice_status,
      days_after: form.days_after !== '' ? parseInt(form.days_after, 10) : 0,
      is_active: true
    };
    
    if (ruleType === 'kdv_odemesi') {
      payload.kdv_odeme_gunu = parseInt(form.kdv_odeme_gunu, 10);
      payload.bildirim_tipi = form.bildirim_tipi;
      if (form.bildirim_tipi === 'gun_kala') {
        payload.bildirim_suresi = parseInt(form.bildirim_suresi, 10);
      }
    }

    try {
      setLoading(true);
      if (editingId) {
        await axios.put(`${API}/api/accounting/warning-rules/${editingId}`, payload, { headers: getAuthHeaders() });
        toast.success('Kural güncellendi');
      } else {
        await axios.post(`${API}/api/accounting/warning-rules`, payload, { headers: getAuthHeaders() });
        toast.success('Kural oluşturuldu');
      }
      handleCloseModal();
      loadRules();
    } catch (e) {
      toast.error('Kural kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kuralı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/accounting/warning-rules/${id}`, { headers: getAuthHeaders() });
      toast.success('Kural silindi');
      loadRules();
    } catch (e) {
      toast.error('Kural silinemedi');
    }
  };

  const toggleBranch = (bid) => {
    const isSelected = form.branch_ids.includes(bid);
    let newIds = [];
    if (isSelected) {
      newIds = form.branch_ids.filter(id => id !== bid);
    } else {
      newIds = [...form.branch_ids, bid];
    }
    setForm({ ...form, branch_ids: newIds, all_branches: newIds.length === 0 });
  };

  const toggleRecord = (rid) => {
    const isSelected = form.target_record_ids.includes(rid);
    let newIds = [];
    if (isSelected) {
      newIds = form.target_record_ids.filter(id => id !== rid);
    } else {
      newIds = [...form.target_record_ids, rid];
    }
    setForm({ ...form, target_record_ids: newIds, all_records: newIds.length === 0 });
  };

  const toggleInvoiceStatus = (status) => {
    const isSelected = form.invoice_status.includes(status);
    let newStatus = [];
    if (isSelected) {
      newStatus = form.invoice_status.filter(s => s !== status);
    } else {
      newStatus = [...form.invoice_status, status];
    }
    setForm({ ...form, invoice_status: newStatus });
  };

  const filteredRecords = pendingRecords.filter(r => 
    r.title.toLowerCase().includes(recordSearch.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{title} Ayarları</h3>
          <p className="text-sm text-gray-500">
            {title} için uyarı koşullarını belirleyin.
          </p>
        </div>
        {!readOnly && (
          <Button onClick={() => handleOpenModal()} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="mr-2 h-4 w-4" /> Kural Ekle
          </Button>
        )}
      </div>

      {rules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center bg-gray-50">
          <p className="text-gray-500">Henüz kural tanımlanmamış.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rules.map(rule => (
            <div key={rule.id} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm relative group hover:border-indigo-300 transition-all">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-semibold text-gray-800 line-clamp-1">{rule.name}</h4>
                {!readOnly && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleOpenModal(rule)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDelete(rule.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="font-medium">Fatura Durumu: </span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {rule.invoice_status?.map(st => (
                        <Badge key={st} variant="secondary" className="font-normal">
                          {st === 'fatura_kesilecek' ? 'Fatura Kesilecek' : 'Eksik Faturalar'}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span><span className="font-medium">Uyarı:</span> İşlemden {rule.days_after} gün sonra</span>
                </div>

                <div className="flex items-start gap-2">
                  <CheckSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="font-medium">Hedef: </span>
                    {!rule.target_record_ids || rule.target_record_ids.length === 0 ? (
                      <span className="text-gray-500 italic">Tüm İşlemler</span>
                    ) : (
                      <span className="text-indigo-600 font-medium">{rule.target_record_ids.length} İşlem Seçili</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-gray-400 mt-0.5" />
                  <div>
                    <span className="font-medium">Şubeler: </span>
                    {!rule.branch_ids || rule.branch_ids.length === 0 ? (
                      <span className="text-gray-500 italic">Tüm Şubeler</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rule.branch_ids.map(bid => {
                          const b = branches.find(x => x.id === bid);
                          return b ? <Badge key={bid} variant="outline" className="font-normal">{b.name}</Badge> : null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Left Column */}
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Kural Adı</Label>
                <Input
                  placeholder="Örn: 7 Gün Geciken Faturalar"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </div>

              {ruleType !== 'kdv_odemesi' && (
                <>
                  <div className="space-y-2">
                    <Label>Fatura Durumu Seçimi</Label>
                    <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="chk-fatura-kesilecek"
                          checked={form.invoice_status.includes('fatura_kesilecek')}
                          onCheckedChange={() => toggleInvoiceStatus('fatura_kesilecek')}
                        />
                        <Label htmlFor="chk-fatura-kesilecek" className="cursor-pointer">Fatura Kesilecek</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="chk-eksik-faturalar"
                          checked={form.invoice_status.includes('eksik_faturalar')}
                          onCheckedChange={() => toggleInvoiceStatus('eksik_faturalar')}
                        />
                        <Label htmlFor="chk-eksik-faturalar" className="cursor-pointer">Eksik Faturalar</Label>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Uyarı Süresi (Gün)</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">İşlem yapıldıktan</span>
                      <Input
                        type="number"
                        min="0"
                        className="w-24 text-center"
                        value={form.days_after}
                        onChange={e => setForm({ ...form, days_after: e.target.value })}
                      />
                      <span className="text-sm text-gray-500">gün sonra uyar</span>
                    </div>
                  </div>
                </>
              )}
              
              {ruleType === 'kdv_odemesi' && (
                <>
                  <div className="space-y-2">
                    <Label>KDV Ödeme Günü</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Her ayın</span>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        className="w-24 text-center"
                        value={form.kdv_odeme_gunu}
                        onChange={e => setForm({ ...form, kdv_odeme_gunu: e.target.value })}
                      />
                      <span className="text-sm text-gray-500">günü</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Bildirim Tipi</Label>
                    <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="chk-her-gun"
                          checked={form.bildirim_tipi === 'her_gun'}
                          onCheckedChange={() => setForm({ ...form, bildirim_tipi: 'her_gun', bildirim_suresi: '' })}
                        />
                        <Label htmlFor="chk-her-gun" className="cursor-pointer">Her Gün Hesap Tut ve Göster</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="chk-gun-kala"
                          checked={form.bildirim_tipi === 'gun_kala'}
                          onCheckedChange={() => setForm({ ...form, bildirim_tipi: 'gun_kala' })}
                        />
                        <Label htmlFor="chk-gun-kala" className="cursor-pointer">Ödeme Gününe Belli Gün Kala Haber Ver</Label>
                      </div>
                    </div>
                  </div>
                  
                  {form.bildirim_tipi === 'gun_kala' && (
                    <div className="space-y-2">
                      <Label>Bildirim Süresi</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          className="w-24 text-center"
                          value={form.bildirim_suresi}
                          onChange={e => setForm({ ...form, bildirim_suresi: e.target.value })}
                        />
                        <span className="text-sm text-gray-500">gün kala uyar</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Geçerli Şubeler</Label>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="chk-all-branches"
                      checked={form.all_branches}
                      onCheckedChange={(chk) => {
                        if (chk) {
                          setForm({ ...form, all_branches: true, branch_ids: [] });
                        } else {
                          setForm({ ...form, all_branches: false });
                        }
                      }}
                    />
                    <Label htmlFor="chk-all-branches" className="text-xs cursor-pointer">Tüm Şubelerde</Label>
                  </div>
                </div>

                {!form.all_branches && (
                  <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 max-h-48 overflow-y-auto">
                    {branches.map(b => (
                      <div key={b.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`b-${b.id}`}
                          checked={form.branch_ids.includes(b.id)}
                          onCheckedChange={() => toggleBranch(b.id)}
                        />
                        <Label htmlFor={`b-${b.id}`} className="text-sm cursor-pointer line-clamp-1">{b.name}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Hedef İşlemler */}
            {ruleType !== 'kdv_odemesi' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Hedef İşlemler</Label>
                  <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="chk-all-records"
                    checked={form.all_records}
                    onCheckedChange={(chk) => {
                      if (chk) {
                        setForm({ ...form, all_records: true, target_record_ids: [] });
                      } else {
                        setForm({ ...form, all_records: false });
                      }
                    }}
                  />
                  <Label htmlFor="chk-all-records" className="text-xs cursor-pointer font-medium text-indigo-600">Tüm İşlemlere Uygula</Label>
                </div>
              </div>

              {!form.all_records && (
                <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col h-[350px]">
                  <div className="p-2 border-b bg-gray-50 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Cari hesap, seri no vb. ara..." 
                      className="pl-9 bg-white"
                      value={recordSearch}
                      onChange={(e) => setRecordSearch(e.target.value)}
                    />
                  </div>
                  <ScrollArea className="flex-1 bg-white">
                    {filteredRecords.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        İşlem bulunamadı
                      </div>
                    ) : (
                      <div className="p-2 space-y-1">
                        {filteredRecords.map(r => (
                          <div 
                            key={r.id} 
                            onClick={() => toggleRecord(r.id)}
                            className={`flex items-start space-x-3 p-2 rounded cursor-pointer hover:bg-gray-50 transition-colors ${
                              form.target_record_ids.includes(r.id) ? 'bg-indigo-50/50' : ''
                            }`}
                          >
                            <Checkbox 
                              checked={form.target_record_ids.includes(r.id)}
                              onCheckedChange={() => toggleRecord(r.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-800 line-clamp-2 leading-snug">
                                {r.title}
                              </p>
                              {r.tarih && <p className="text-xs text-gray-500 mt-0.5">Tarih: {r.tarih}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                  <div className="p-2 border-t bg-gray-50 text-xs text-gray-500 text-center">
                    {form.target_record_ids.length} işlem seçildi
                  </div>
                </div>
              )}
            </div>
            )}

          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal} disabled={loading}>
              İptal
            </Button>
            <Button onClick={handleSave} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingInvoiceWarningSettings;
